import { BellRing, Circle, Eye, EyeOff, Flag, MessageSquareHeart, Play, Send, X } from 'lucide-react';
import { formatDateTime, timeAgo } from '../qualityConfig';

const firstCompleted = (task) => task.items.map((i) => i.first_completed_at).filter(Boolean).sort()[0];
const startedAt = (task) => task.started_at || firstCompleted(task);

const NudgeLine = ({ t }) => (t.nudge_count > 0 ? (
    <p className="flex items-center gap-1 text-amber-600 dark:text-amber-400" data-testid={`task-nudged-${t.id}`}>
        <BellRing className="h-3 w-3" />Nudged {timeAgo(t.last_nudged_at)}{t.nudge_count > 1 ? ` · ${t.nudge_count}×` : ''}
    </p>
) : null);

export const ActivityCell = ({ t }) => {
    if (!t.opened) {
        return (
            <div className="whitespace-nowrap text-xs" data-testid={`task-activity-${t.id}`}>
                <p className="flex items-center gap-1.5 font-semibold text-rose-600 dark:text-rose-400">
                    <EyeOff className="h-3.5 w-3.5" />{t.status === 'missed' ? 'Never opened' : 'Not opened'}
                </p>
                <p className="text-muted-foreground">Assigned {timeAgo(t.created_at)}</p>
                <NudgeLine t={t} />
            </div>
        );
    }
    const latest = t.completed_at ? ['Finished', t.completed_at] : startedAt(t) ? ['Started', startedAt(t)] : null;
    return (
        <div className="whitespace-nowrap text-xs" data-testid={`task-activity-${t.id}`}>
            <p className="flex items-center gap-1.5 font-medium text-indigo-600 dark:text-indigo-300">
                <Eye className="h-3.5 w-3.5" />Seen {timeAgo(t.seen_at || startedAt(t))}
            </p>
            {latest && <p className="text-muted-foreground">{latest[0]} {timeAgo(latest[1])}</p>}
            {!t.completed_at && <NudgeLine t={t} />}
        </div>
    );
};

const steps = (task) => [
    { id: 'assigned', label: 'Assigned', at: task.created_at, note: `by ${task.assigned_by_name}`, icon: Send },
    { id: 'opened', label: 'Opened by recruiter', at: task.seen_at || (task.opened ? startedAt(task) : null), icon: Eye },
    { id: 'started', label: 'Started a simulation', at: startedAt(task), icon: Play },
    { id: 'completed', label: 'Completed every simulation', at: task.completed_at, icon: Flag },
    { id: 'feedback', label: 'Shared feedback', at: task.feedback?.submitted_at, icon: MessageSquareHeart },
];

const dotTone = (done, missed) => (done ? 'bg-violet-600 text-white'
    : missed ? 'bg-rose-100 text-rose-600 dark:bg-rose-950 dark:text-rose-400' : 'bg-muted text-muted-foreground');

export const ActivityTimeline = ({ task }) => {
    const missed = task.status === 'missed';
    return (
        <div data-testid="task-activity-timeline">
            <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Recruiter activity</p>
            <ol className="ml-2.5 space-y-3 border-l pl-5">
                {steps(task).map((s) => {
                    const done = Boolean(s.at);
                    const Icon = done ? s.icon : missed ? X : Circle;
                    return (
                        <li key={s.id} className="relative" data-testid={`timeline-step-${s.id}`} data-done={done}>
                            <span className={`absolute -left-[31px] top-0.5 grid h-5 w-5 place-items-center rounded-full ring-4 ring-background ${dotTone(done, missed)}`}>
                                <Icon className="h-2.5 w-2.5" />
                            </span>
                            <p className={`text-sm ${done ? 'font-medium' : 'text-muted-foreground'}`}>{s.label}</p>
                            <p className="text-xs text-muted-foreground">
                                {done ? `${formatDateTime(s.at)}${s.note ? ` · ${s.note}` : ''}` : missed ? 'Did not happen before the deadline' : 'Waiting'}
                            </p>
                        </li>
                    );
                })}
            </ol>
            {task.nudge_count > 0 && (
                <p className="mt-3 flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-300" data-testid="detail-nudge-summary">
                    <BellRing className="h-3.5 w-3.5" /> {task.nudge_count} reminder{task.nudge_count > 1 ? 's' : ''} sent · last {formatDateTime(task.last_nudged_at)} by {task.last_nudged_by_name}
                </p>
            )}
        </div>
    );
};
